/**
 * M2 P2: stations + docking. Pure, deterministic, engine-agnostic (no Three.js),
 * mirroring ship.ts so the same logic drives the client now and a server later.
 *
 * A StationSpec sits at a fixed OFFSET from a host body (the client resolves it to
 * a world position each frame via World.get(hostBody).worldPos + offset). The
 * docking sequence is a pure transition function over the player's position/velocity
 * RELATIVE to the station plus the player's input intent — no state mutation, no IO.
 *
 *   IDLE -> REQUESTED -> CLEARED -> APPROACH -> DOCKED -> UNDOCKING -> IDLE
 */
import { sub, length, dot, scale, normalize, type Vec3 } from './vec3.js';

export type StationService = 'Refuel' | 'Repair' | 'Rearm' | 'Market';

export interface StationSpec {
  id: string;
  name: string;
  /** Manifest body name the station is anchored to (e.g. 'Earth'). */
  hostBody: string;
  /** Fixed offset from the host body centre, sim units (km), world frame. */
  offset: Vec3;
  /** Hard dock capture radius (km). Inside this + slow => DOCKED. */
  dockingRadius: number;
  /** Soft approach corridor radius (km). Inside this => may REQUEST. */
  approachRadius: number;
  services: StationService[];
}

/** Explicit docking state machine phases. */
export type DockingPhase =
  | 'IDLE' // no interaction
  | 'REQUESTED' // player asked for clearance (within approachRadius)
  | 'CLEARED' // station granted; approach corridor open
  | 'APPROACH' // inside dockingRadius, still too fast to capture
  | 'DOCKED' // captured; services available; ship parked
  | 'UNDOCKING'; // releasing; one push-off tick before IDLE

export interface DockingState {
  phase: DockingPhase;
  /** id of the station this state refers to (or null in IDLE). */
  stationId: string | null;
}

/** Player-intent edges from input (one per frame; all false = no intent). */
export interface DockingIntent {
  /** dock key pressed while not docked: request docking when eligible. */
  request: boolean;
  /** dock key pressed while DOCKED: begin undock. */
  undock: boolean;
}

/** Kinematics of the player RELATIVE to a station, precomputed by the caller. */
export interface DockingKinematics {
  /** Distance from ship to station centre (km). */
  distanceKm: number;
  /** Ship speed magnitude (km/s). */
  speedKmS: number;
  /**
   * Alignment 0..1: how well the ship's facing points AT the station.
   * dot(unit(shipForward), unit(toStation)), clamped to [0, 1]. 1 = dead-on.
   */
  alignment: number;
}

/** Station + the ship's kinematics relative to it (the active docking target). */
export interface StationContext {
  spec: StationSpec;
  kin: DockingKinematics;
}

// --- Concrete gates (documented, not magic numbers). Tunable, ship as-is. ---
/** Speed under which a docking REQUEST is allowed (must be "arriving"). */
export const REQUEST_MAX_SPEED_KMS = 2.0;
/** Speed under which final capture (=> DOCKED) occurs (near-zero relative vel). */
export const DOCK_MAX_SPEED_KMS = 0.5;
/** Minimum facing alignment to request (must be pointing at the station). */
export const MIN_ALIGNMENT = 0.6; // ~53° cone
/** Hysteresis: leaving this multiple of approachRadius aborts a request. */
export const ABORT_RADIUS_FACTOR = 1.25;

// --- The M2 starter stations: 3 ports anchored to real manifest bodies ---
// Offsets (km) place each station clearly outside its host body's surface
// (Earth r=6371, Mars r=3389.5, Luna r=1737.4) but inside its neighbourhood.

/** Earth's main port — full services, the player's first dock. */
export const STATION_HIGH_GUARD: StationSpec = {
  id: 'high-guard',
  name: 'High Guard Orbital',
  hostBody: 'Earth',
  offset: { x: 12_000, y: 2_000, z: 0 }, // ~12,166 km from centre (~1.9x radius)
  dockingRadius: 8,
  approachRadius: 600,
  services: ['Refuel', 'Repair', 'Rearm', 'Market'],
};

/** Mars logistics depot. */
export const STATION_REDSAND: StationSpec = {
  id: 'redsand',
  name: 'Red Sand Depot',
  hostBody: 'Mars',
  offset: { x: -7_000, y: 1_200, z: 1_500 }, // ~7,278 km from centre
  dockingRadius: 6,
  approachRadius: 500,
  services: ['Refuel', 'Repair', 'Market'],
};

/** Luna relay — smallest of the three, minimal services. */
export const STATION_LUNA_RELAY: StationSpec = {
  id: 'luna-relay',
  name: 'Selene Relay',
  hostBody: 'Luna',
  offset: { x: 3_000, y: 800, z: -1_000 }, // ~3,259 km from centre
  dockingRadius: 5,
  approachRadius: 400,
  services: ['Refuel', 'Repair'],
};

export const STATIONS: StationSpec[] = [STATION_HIGH_GUARD, STATION_REDSAND, STATION_LUNA_RELAY];

// --- pure functions (kept consecutive + compact: each < 50 lines) ---

export const initialDockingState = (): DockingState => ({ phase: 'IDLE', stationId: null });

export const stationById = (id: string): StationSpec | undefined =>
  STATIONS.find((s) => s.id === id);

/** Eligible to REQUEST: in the corridor, slow enough, and pointing at the dock. */
const requestEligible = (spec: StationSpec, kin: DockingKinematics): boolean =>
  kin.distanceKm <= spec.approachRadius &&
  kin.speedKmS <= REQUEST_MAX_SPEED_KMS &&
  kin.alignment >= MIN_ALIGNMENT;

/**
 * Pure transition. Given the current state, the station the ship is nearest to
 * (or null if none within approachRadius), and player intent => next state.
 * Deterministic and side-effect free.
 */
export const stepDocking = (
  state: DockingState,
  ctx: StationContext | null,
  intent: DockingIntent,
): DockingState => {
  // No station in range: non-docked states collapse to IDLE; DOCKED/UNDOCKING
  // are preserved (a docked ship is parked, so range math may be degenerate).
  if (!ctx) {
    const parked = state.phase === 'DOCKED' || state.phase === 'UNDOCKING';
    return parked ? state : initialDockingState();
  }
  const { spec, kin } = ctx;
  const id = spec.id;
  const sameStation = state.stationId === id;
  const inCorridor = sameStation && kin.distanceKm <= spec.approachRadius * ABORT_RADIUS_FACTOR;

  switch (state.phase) {
    case 'IDLE':
      return intent.request && requestEligible(spec, kin)
        ? { phase: 'REQUESTED', stationId: id }
        : state;
    case 'REQUESTED': // auto-grant (no station AI in P2); abort if out of corridor
      return inCorridor ? { phase: 'CLEARED', stationId: id } : initialDockingState();
    case 'CLEARED':
      if (!inCorridor) return initialDockingState();
      return kin.distanceKm <= spec.dockingRadius ? { phase: 'APPROACH', stationId: id } : state;
    case 'APPROACH':
      if (!sameStation) return initialDockingState();
      if (kin.distanceKm > spec.dockingRadius) return { phase: 'CLEARED', stationId: id };
      return kin.speedKmS <= DOCK_MAX_SPEED_KMS ? { phase: 'DOCKED', stationId: id } : state;
    case 'DOCKED':
      return intent.undock ? { phase: 'UNDOCKING', stationId: id } : state;
    case 'UNDOCKING': // one push-off tick (client nudges) then release
      return initialDockingState();
    default:
      return state;
  }
};

/**
 * Build kinematics from raw Float64 vectors.
 * @param shipForward world-frame forward (rotateVec3(orientation,(0,0,-1))).
 */
export const dockingKinematics = (
  shipPos: Vec3,
  shipVel: Vec3,
  stationPos: Vec3,
  shipForward: Vec3,
): DockingKinematics => {
  const toStation = sub(stationPos, shipPos);
  const distanceKm = length(toStation);
  const speedKmS = length(shipVel);
  const dir = distanceKm > 0 ? scale(toStation, 1 / distanceKm) : toStation;
  const alignment = Math.max(0, dot(normalize(shipForward), dir));
  return { distanceKm, speedKmS, alignment };
};

/**
 * Find the nearest station within approachRadius and build its StationContext.
 * Returns null if none in range. `resolvePos` maps a spec to its current world
 * position (host body world pos + offset) so sim-core stays World-agnostic.
 */
export const nearestStationContext = (
  stations: readonly StationSpec[],
  resolvePos: (s: StationSpec) => Vec3,
  shipPos: Vec3,
  shipVel: Vec3,
  shipForward: Vec3,
): StationContext | null => {
  let best: StationContext | null = null;
  for (const spec of stations) {
    const kin = dockingKinematics(shipPos, shipVel, resolvePos(spec), shipForward);
    if (kin.distanceKm > spec.approachRadius) continue;
    if (!best || kin.distanceKm < best.kin.distanceKm) best = { spec, kin };
  }
  return best;
};
